import { useMemo } from "react";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import {
  Subscription,
  subscriptionProductsById,
} from "@pages/about-products/about-products-filter-options";
import { AboutProductsTableEntity } from "@pages/about-products/about-products-table-types";

/**
 * Builds the about-products table rows by mapping each feature to its
 * subscription and bundle availability flags.
 *
 * @param allFeatures the features shown as table rows
 * @param allBundles all bundles, used to compute subscription availability
 * @param visibleBundles the bundles rendered as availability columns
 * @param subscriptions the subscriptions rendered as availability columns
 * @returns the row entities to be used in the about-products table
 */
export function useAboutProductsTableRows(
  allFeatures: FaroCoreApiTypes.AdmFeature[],
  allBundles: FaroCoreApiTypes.AdmBundle[],
  visibleBundles: FaroCoreApiTypes.AdmBundle[],
  subscriptions: Subscription[]
): AboutProductsTableEntity[] {
  return useMemo(() => {
    return allFeatures.map((feature) => {
      const subscriptionFields: Record<string, boolean> = {};
      for (const { id } of subscriptions) {
        subscriptionFields[id] = subscriptionProductsById[id].identifiers.some(
          (identifier) => {
            if (identifier === feature.identifier) {
              return true;
            }

            const bundle = allBundles.find((b) => b.identifier === identifier);
            return bundle?.features.some((f) => f.id === feature.id) ?? false;
          }
        );
      }

      const bundleFields: Record<string, boolean> = {};
      for (const bundle of visibleBundles) {
        bundleFields[bundle.id] = bundle.features.some(
          (featureBundle) => featureBundle.id === feature.id
        );
      }

      return {
        id: feature.id,
        featureName: feature.name,
        featureIdentity: feature.identifier,
        featureDescription: feature.description ?? "",
        ...subscriptionFields,
        ...bundleFields,
      };
    });
  }, [allFeatures, allBundles, visibleBundles, subscriptions]);
}
